/* eslint-disable @typescript-eslint/no-this-alias */
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  BulkImportType,
  ChartDataPoint,
  DataChip,
  DataSeries,
  DialogData,
  GraphDataPoint,
  GraphDataResponse,
  GraphTimeSeriesData,
  HourlyTimeSlots,
  TimeSlotData,
  TimeSlotSeriesData
} from '@dr-customer-offers-ui/lib-interfaces';
import { Actions as EffectActions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import ExportData from 'highcharts/modules/export-data';
import { default as ExportingModule, default as HC_exporting } from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment-timezone';
import { filter, first, startWith } from 'rxjs';
import { TabOption } from '../../../../shared/models';
import { DataViewModelService } from '../../../../shared/services/data-vm.service';
import { DateService } from '../../../../shared/services/date.service';
import { InternalService } from '../../../../shared/services/internal.service';
import { MessageService } from '../../../../shared/services/message.service';
import * as Actions from '../../../../store/actions/com.actions';
import * as Selectors from '../../../../store/selectors/com.selectors';
import { FiltersStateService } from '../../../filters/filters-state.service';
import { GraphEditDialogComponent } from '../dialog/dialog.component';
import { OfferRowData } from './component/open-graph-table/open-graph-table.component';
import { OpenGraphService } from './open-graph.service';

HC_exporting(Highcharts);
ExportingModule(Highcharts);
OfflineExporting(Highcharts);
ExportData(Highcharts);
HighchartsMore(Highcharts);

// Custom horizontal-dash marker, used for the PRT capacity increased/decreased
// marks on the graph (the green/red dashes in the design). Registered once.
if (!(Highcharts as any).SVGRenderer.prototype.symbols.hdash) {
  (Highcharts as any).SVGRenderer.prototype.symbols.hdash = (x: number, y: number, w: number, h: number): (string | number)[] => {
    const thickness = Math.max(2, Math.round(h / 3));
    const cy = y + Math.round((h - thickness) / 2);
    return ['M', x, cy, 'L', x + w, cy, 'L', x + w, cy + thickness, 'L', x, cy + thickness, 'Z'];
  };
}

@Component({
  selector: 'dr-customer-offers-ui-open-graph',
  templateUrl: './open-graph.component.html',
  styleUrls: ['./open-graph.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpenGraphComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private store = inject(Store);

  Highcharts: typeof Highcharts = Highcharts;
  chartRef: Highcharts.Chart | undefined;
  // Signals for reactive state management with OnPush
  protected plotBandsSignal = signal<Highcharts.XAxisPlotBandsOptions[]>([]);
  protected optOutPlotBandsSignal = signal<Highcharts.XAxisPlotBandsOptions[]>([]);
  private offerResponseSignal: WritableSignal<GraphDataResponse | null> = signal(null);
  private chartSeriesSignal = signal<DataSeries[]>([]);
  private chartUpdateTrigger = signal<number>(0);
  private apiMetadata = signal<any>(null);
  private currentDateSignal = signal<moment.Moment | null>(null);
  private originalApiDataSignal = signal<GraphTimeSeriesData[]>([]);
  protected resultsFromDialogSignal = signal<any>(null);
  protected submitLoadingSignal: WritableSignal<boolean> = signal(false);
  protected showDownloadUploadOptionsSignal = signal(false);
  protected tableSeriesSignal = signal<OfferRowData[]>([]);
  protected availableChipsSignal = signal<DataChip[]>([]);
  private graphOffersLoading = toSignal(this.store.select(Selectors.selectGraphOffersLoading), { initialValue: false });
  private currentRegViewConfigSignal = toSignal(this.store.select(Selectors.selectCurrentRegViewConfig), { initialValue: null });
  protected timeZoneSignal = computed(() => this.currentRegViewConfigSignal()?.timeZone || 'UTC');
  protected offerDimensionsSignal = computed<string[]>(() => this.currentRegViewConfigSignal()?.graph_config?.offer_dimensions ?? []);

  protected isConsolidatedToHourly = computed<boolean>(() => this.graphService.isConsolidatedToHourly(this.currentRegViewConfigSignal()));
  protected isSplitDimsAbsentSignal = computed<boolean>(() => this.graphService.isSplitDimsAbsent(this.currentRegViewConfigSignal()));

  protected readonly optOutColor = 'rgba(128, 128, 128, 0.25)';
  protected readonly gateClosedColor = OpenGraphService.GATE_CLOSED_COLOR;

  protected isBulkImportAllowed = computed(() => {
    const allowImport = this.currentRegViewConfigSignal()?.gateRules?.post_gate_edits?.allow_offer_edit;
    return !!allowImport && !!this.currentRegViewConfigSignal()?.allow_bulk_upload;
  });

  protected showBaselineSignal = computed<boolean>(() => this.graphService.shouldOverlayBaseline(this.currentRegViewConfigSignal()));

  private userLocale = computed(() => this.internalService.userPreferenceSignal()?.locale ?? 'en-US');
  protected currencySignal = computed(() => this.graphService.resolveCurrency(this.currentRegViewConfigSignal()?.timeZone));
  form: FormGroup;

  // Computed chart options that automatically update when chips change
  chartOptions = computed(() => {
    // Trigger dependency on chartUpdateTrigger for forced updates
    this.chartUpdateTrigger();

    // If no data, return empty chart configuration
    if (!this.hasDataSignal()) {
      return {
        ...this.getBaseChartOptions(),
        series: []
      } as Highcharts.Options;
    }

    const visibleSeries = this.chartSeriesSignal()
      .filter((series: DataSeries) => series.visible && series.id !== OpenGraphService.BASELINE_DIM_ID)
      .map((series) => {
        const baseSeriesOptions: any = {
          id: series.id,
          name: series.name,
          data: series.data,
          color: series.color,
          type: series.chartType,
          unit: series.unit, // Custom property for tooltip
          cursor: 'pointer' // Show pointer cursor for clickable data points
        };

        // Add type-specific properties conditionally
        if (series.chartType === 'line') {
          baseSeriesOptions.lineWidth = 2;
          baseSeriesOptions.marker = {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 4
              }
            }
          };
        } else if (series.chartType === 'column') {
          baseSeriesOptions.borderWidth = 1; // Add border to each column
          baseSeriesOptions.borderColor = '#ffffff'; // White border for contrast
          baseSeriesOptions.pointPadding = 0; // No space between bars
          baseSeriesOptions.groupPadding = 0; // No space between groups
          baseSeriesOptions.borderRadius = 0; // Square corners
        }

        return baseSeriesOptions;
      });

    return {
      ...this.getBaseChartOptions(),
      series: [...visibleSeries, ...this.getPrtCapacityMarkerSeries(), ...this.getPrtCapacityMarkerSeries()]
    } as Highcharts.Options;
  });

  protected dstStatusSignal = this.dateService.dstStatusSignal;

  isPreviousDayDisabled = computed(() => {
    const currentDate = this.currentDateSignal();
    const startDate = this.dataVMService.getAppliedDayRange()?.start;

    if (!currentDate || !startDate) {
      return true;
    }

    return currentDate.isSameOrBefore(startDate, 'day');
  });

  isNextDayDisabled = computed(() => {
    const currentDate = this.currentDateSignal();
    const endDate = this.dataVMService.getAppliedDayRange()?.end;

    if (!currentDate || !endDate) {
      return true;
    }

    return currentDate.isSameOrAfter(endDate, 'day');
  });

  previousButtonTooltip = computed(() => {
    if (this.isPreviousDayDisabled()) {
      const startDate = this.dataVMService.getAppliedDayRange()?.start;
      return startDate ? `Cannot go before ${startDate.format('MM/DD/YYYY')}` : 'Cannot go to previous day';
    }
    return 'Go to previous day';
  });

  nextButtonTooltip = computed(() => {
    if (this.isNextDayDisabled()) {
      const endDate = this.dataVMService.getAppliedDayRange()?.end;
      return endDate ? `Cannot go after ${endDate.format('MM/DD/YYYY')}` : 'Cannot go to next day';
    }
    return 'Go to next day';
  });

  // Component state signals
  loadingSignal = computed(() => this.graphOffersLoading());
  hasDataSignal = signal<boolean>(true);
  loadedDate = moment.utc();

  // Getters
  get selectedTabSignal(): WritableSignal<TabOption> {
    return this.dataVMService.selectedTabOptionSignal;
  }

  constructor(
    private dataVMService: DataViewModelService,
    private filterService: FiltersStateService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private messageService: MessageService,
    private translate: TranslateService,
    private dateService: DateService,
    private internalService: InternalService,
    private actions$: EffectActions,
    private graphService: OpenGraphService
  ) {
    this.form = this.fb.group({
      currentDate: moment.utc(this.dataVMService.getAppliedDayRange()?.start)
    });

    this.currentDateSignal.set(moment.utc(this.currentDateFormControl.value));

    this.currentDateFormControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.currentDateSignal.set(moment.utc(value));
    });
  }

  get currentDateFormControl(): FormControl<moment.Moment> {
    return this.form.get('currentDate') as FormControl<moment.Moment>;
  }

  get formattedCurrentDate(): string {
    const date = this.currentDateFormControl.value;
    const userLocale = this.userLocale();
    return date ? date.locale(userLocale).format('L (ddd)') : '';
  }

  ngOnInit(): void {
    this.actions$.pipe(ofType(Actions.bulkImportCompleted), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const currentSelectedDate = this.currentDateFormControl.value ?? this.dataVMService.getAppliedDayRange()?.start;
      if (currentSelectedDate) {
        this.loadedDate = moment.utc(currentSelectedDate);
        this.loadGraphData(moment.utc(currentSelectedDate));
      }
    });

    this.dataVMService.selectedFromDayPickerFormControl.valueChanges
      .pipe(startWith(this.dataVMService.selectedFromDayPickerFormControl.value), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const appliedStart = this.dataVMService.getAppliedDayRange()?.start;
        if (appliedStart) {
          this.currentDateFormControl.setValue(appliedStart, { emitEvent: false });
        }
      });

    this.store
      .select(Selectors.selectCurrentRegViewConfig)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((filters) => filters !== null && filters !== undefined)
      )
      .subscribe(() => {
        const currentSelectedDate = this.dataVMService.getAppliedDayRange()?.start;
        if (currentSelectedDate) {
          this.currentDateFormControl.setValue(currentSelectedDate, { emitEvent: false });
          this.loadedDate = currentSelectedDate;
          this.loadGraphData(currentSelectedDate);
        }
      });

    this.store
      .select(Selectors.selectCurrentGraphOffers)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: GraphDataResponse | null) => {
        this.offerResponseSignal.set(response);
        this.processApiResponse(response);
        this.filterService.setApplyButtonChanging(this.dataVMService.selectedRegFormControl.value);
      });

    // Handle loading errors
    this.store
      .select(Selectors.selectGraphOffersError)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((error) => error !== null)
      )
      .subscribe(() => {
        this.offerResponseSignal.set(null);
        this.hasDataSignal.set(false);
      });
  }

  /**
   * Load graph data from API response
   */
  private loadGraphData(currentDate: moment.Moment): void {
    this.resultsFromDialogSignal.set(null);

    const utcString: string = currentDate.format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';
    // Dispatch action to load graph offers
    this.store.dispatch(
      Actions.loadGraphOffers({
        currentDate: utcString
      })
    );
  }

  /**
   * Handle chart click events and open dialog
   */
  private onChartClick = (event: any) => {
    const x = (event as Highcharts.ChartClickEventObject).xAxis?.[0]?.value;
    if (x == null || !this.shouldOpenAt(x)) return;
    const clickedTime = moment.tz(x, this.timeZoneSignal());
    const dialogData = this.prepareFullDayDialogData(clickedTime);
    this.openEditDialog(dialogData);
  };

  private onDataPointClick(point: any, event: any): void {
    event.stopPropagation();
    if (point.x == null || !this.shouldOpenAt(point.x)) return;

    const clickedTime = moment.tz(point.x, this.timeZoneSignal());

    const dialogData = this.prepareFullDayDialogData(clickedTime);
    this.openEditDialog(dialogData);
  }

  /**
   * Check if the x value should open the editor dialog
   */
  private shouldOpenAt(x: unknown): x is number {
    return typeof x === 'number' && !isNaN(x) && !this.isInGateClosedZone(x);
  }

  /**
   * Check if the x value is inside gate closed zone
   */
  private isInGateClosedZone(x: number): boolean {
    return this.plotBandsSignal().some(({ from, to }) => typeof from === 'number' && typeof to === 'number' && x >= from && x < to);
  }

  /**
   * Prepare full day data for dialog based on clicked time
   */
  private prepareFullDayDialogData(clickedTime: moment.Moment): DialogData {
    const metadata = this.apiMetadata();
    const frequency = metadata?.frequency || 15;
    const currentDateTz = moment.tz(clickedTime, this.timeZoneSignal()).startOf('day');

    // Generate all 24 hours of data
    const allHoursData: HourlyTimeSlots[] = [];

    for (let hour = 0; hour <= 24; hour++) {
      const hourStart = currentDateTz.clone().add(hour, 'hours');
      const hourData = this.generateHourlyTimeSlots(hourStart, frequency);
      allHoursData.push(hourData);
    }

    const clickedHour = moment.tz(clickedTime, this.timeZoneSignal()).hour();
    const currentHourData = allHoursData.find((hourData) => hourData.hour === clickedHour) || allHoursData[0];

    return {
      clickedTime: moment.tz(clickedTime, this.timeZoneSignal()),
      currentHourData,
      allHoursData,
      currency: this.currencySignal(),
      metadata: {
        frequency,
        unit: metadata?.unit || 'kW',
        date: this.loadedDate,
        timezone: this.timeZoneSignal()
      },
      isOptOutEnabled: this.currentRegViewConfigSignal()?.enable_opt_out ?? true,
      isConsolidatedToHourly: this.isConsolidatedToHourly()
    };
  }

  private resolveOptOutForTime(time: moment.Moment): boolean | null {
    const t = moment.utc(time).valueOf();
    for (const series of this.originalApiDataSignal()) {
      const dp = series.dataPoints?.find((p) => {
        const start = moment.utc(p.start_dttm_utc).valueOf();
        const end = moment.utc(p.end_dttm_utc).valueOf();
        return t >= start && t < end;
      });
      if (dp?.offer_opt_out_state !== undefined) {
        return dp.offer_opt_out_state;
      }
    }
    return null;
  }
  /**
   * Generate hourly time slots data for a specific hour
   */
  private generateHourlyTimeSlots(hourStart: moment.Moment, frequency: number): HourlyTimeSlots {
    const hour = hourStart.hour();
    const slotsPerHour = 60 / frequency; // e.g., 4 slots for 15-min frequency
    const timeSlots: TimeSlotData[] = [];

    for (let i = 0; i < slotsPerHour; i++) {
      const slotTime = hourStart
        .clone()
        .tz(this.timeZoneSignal())
        .add(i * frequency, 'minutes');

      const slotData: TimeSlotData = {
        time: slotTime,
        formattedTime: slotTime.format('HH:mm'),
        data: this.getDataForTimeSlot(slotTime),
        offer_opt_out_state: this.resolveOptOutForTime(slotTime)
      };
      timeSlots.push(slotData);
    }
    return {
      hour,
      hourLabel: hourStart.format('HH:00'),
      timeSlots
    };
  }

  /**
   * Get data for a specific time slot from all series with original timestamps
   */
  private getDataForTimeSlot(timeSlot: moment.Moment): TimeSlotSeriesData[] {
    return this.chartSeriesSignal()
      .filter((series) => series.id !== OpenGraphService.BASELINE_DIM_ID)
      .map((series) => {
        const chartDataPoint = series.data.find((point) => moment.tz(point.x, this.timeZoneSignal()).isSame(timeSlot, 'minute'));
        return {
          seriesName: series.name,
          value: chartDataPoint?.y ?? null,
          unit: series.unit,
          color: series.color,
          chartType: series.chartType
        };
      });
  }

  /**
   * Open edit dialog with prepared data
   */
  private openEditDialog(dialogData: DialogData): void {
    const dialogRef = this.dialog.open(GraphEditDialogComponent, {
      data: dialogData,
      width: '75rem',
      disableClose: true,
      maxWidth: '90vw',
      maxHeight: '90vh'
    });

    dialogRef
      .afterClosed()
      .pipe(first())
      .subscribe((result) => {
        if (result) {
          this.handleDialogResult(result);
        }
      });
  }

  /**
   * Handle dialog result after user saves changes
   * Updates the chart data with new values and redraws the chart
   */
  private handleDialogResult(result: any): void {
    if (Array.isArray(result.data) && result.data.length > 0) {
      const optOutByStart = new Map<number, boolean>(
        result.data.map((d: any) => [moment.utc(d.offer_start_dttm_utc).valueOf(), !!d.offer_opt_out_state])
      );

      const patched = this.originalApiDataSignal().map((series: GraphTimeSeriesData) => ({
        ...series,
        dataPoints: series.dataPoints?.map((p: GraphDataPoint) => {
          const flag = optOutByStart.get(moment.utc(p.start_dttm_utc).valueOf());
          return flag === undefined ? p : { ...p, offer_opt_out_state: flag };
        })
      }));

      this.originalApiDataSignal.set(patched);
      this.optOutPlotBandsSignal.set(this.buildOptOutPlotBands());
    }

    this.resultsFromDialogSignal.update((results) => {
      if (!results) return result;

      // Check if any existing data item has the same offer_start_dttm_utc
      const existingDataIndices: number[] =
        results.data?.reduce((indices: number[], dataItem: any, index: number) => {
          const hasMatch = result.data?.some((resultItem: any) => resultItem.offer_start_dttm_utc === dataItem.offer_start_dttm_utc);

          if (hasMatch) {
            indices.push(index);
          }

          return indices;
        }, []) ?? [];

      let updatedData: any[];

      if (existingDataIndices.length > 0) {
        // Have MULTIPLE indices, remove all matching items
        updatedData = results.data?.filter((_: any, index: number) => !existingDataIndices.includes(index)) || [];

        // Add ALL new items from result.data
        updatedData = [...updatedData, ...(result.data || [])];
      } else {
        updatedData = [...(results.data || []), ...(result.data || [])];
      }

      return {
        ...results,
        changes: [...(results.changes || []), ...(result.changes || [])],
        affectedHours: [...(results.affectedHours || []), ...(result.affectedHours || [])],
        data: updatedData,
        totalChanges: (results.totalChanges || 0) + (result.totalChanges || 0),
        clickedTime: result.clickedTime || results.clickedTime
      };
    });

    // Get current chart series data
    const currentSeries = this.chartSeriesSignal();
    const updatedSeries = [...currentSeries];

    // Process each change and update the corresponding series data
    (result.changes || []).forEach((change: any) => {
      const { newValue, seriesName, time } = change;

      // Find the series to update
      const seriesIndex = updatedSeries.findIndex((series) => series.name === seriesName);

      if (seriesIndex === -1) {
        console.warn(`Series ${seriesName} not found`);
        return;
      }

      // Create a copy of the series data
      const seriesData = [...updatedSeries[seriesIndex].data];

      // Create the timestamp to match against
      const targetTimestamp = moment.utc(time).valueOf();

      // Find the data point to update
      const dataPointIndex = seriesData.findIndex((point) => {
        const pointTime = moment.utc(point.x);
        return pointTime.valueOf() === targetTimestamp;
      });

      if (dataPointIndex !== -1) {
        // Update existing data point
        seriesData[dataPointIndex] = {
          ...seriesData[dataPointIndex],
          y: newValue !== null && newValue !== undefined ? Number(newValue) : 0
        };
      } else {
        // If data point doesn't exist, create a new one (edge case)
        const newDataPoint: ChartDataPoint = {
          x: targetTimestamp,
          y: newValue !== null && newValue !== undefined ? Number(newValue) : 0
        };

        seriesData.push(newDataPoint);
        // Sort the data by timestamp to maintain order
        seriesData.sort((a, b) => a.x - b.x);
      }

      // Update the series with new data
      updatedSeries[seriesIndex] = {
        ...updatedSeries[seriesIndex],
        data: seriesData
      };
    });

    // Update the signal with new series data
    this.chartSeriesSignal.set(updatedSeries);
    this.rebuildTableSeriesFromChart();

    // Force chart update by triggering the computed signal
    this.chartUpdateTrigger.set(this.chartUpdateTrigger() + 1);

    // If using manual chart update, call it
    if (this.chartRef) {
      this.updateChartManually();
    }
  }

  saveChangesToAPI(): void {
    this.submitLoadingSignal.set(true);
    this.dataVMService
      .putGraphDataOffer(this.resultsFromDialogSignal().data)
      .pipe(first())
      .subscribe({
        next: () => {
          {
            this.messageService.handleSuccess(this.translate.instant('COM.TABS.OPEN.CHART.SUCCESS_TEXT'));
            this.submitLoadingSignal.set(false);
            this.resultsFromDialogSignal.set(null);
          }
        },
        error: () => {
          this.submitLoadingSignal.set(false);
          this.onCancel();
        }
      });
  }

  onCancel(): void {
    this.resultsFromDialogSignal.set(null);
    this.processApiResponse(this.offerResponseSignal());
  }

  private buildOptOutPlotBands(): Highcharts.XAxisPlotBandsOptions[] {
    const source = this.originalApiDataSignal().find((s) => s.dataPoints?.length)?.dataPoints ?? [];

    const intervals = source
      .filter((p) => p.offer_opt_out_state === true)
      .map((p) => ({ from: moment.utc(p.start_dttm_utc).valueOf(), to: moment.utc(p.end_dttm_utc).valueOf() }))
      .sort((a, b) => a.from - b.from);

    const merged: { from: number; to: number }[] = [];
    for (const iv of intervals) {
      const last = merged[merged.length - 1];
      if (last && iv.from <= last.to) last.to = Math.max(last.to, iv.to);
      else merged.push({ ...iv });
    }

    return merged.map((m) => ({
      from: m.from,
      to: m.to,
      color: this.optOutColor,
      zIndex: 0
    }));
  }

  /**
   * Process API response and convert to chart format
   */
  private processApiResponse(response: GraphDataResponse | null): void {
    if (!response || !response.timeSeriesData) {
      console.warn('Invalid API response structure');
      this.hasDataSignal.set(false);
      return;
    }

    this.apiMetadata.set(response.metadata);

    const currency = this.currencySignal();
    const offerDimensions = this.currentRegViewConfigSignal()?.graph_config?.offer_dimensions ?? [];
    const chips = this.graphService.buildChips(response, currency, offerDimensions);

    this.availableChipsSignal.set(this.graphService.getVisibleChips(chips));

    this.storeOriginalApiData(response.timeSeriesData);

    this.chartSeriesSignal.set(this.graphService.buildChartSeries(response, chips));
    this.rebuildTableSeriesFromChart();
    this.hasDataSignal.set(true);
    this.plotBandsSignal.set(this.graphService.buildPlotBands(response));
    this.optOutPlotBandsSignal.set(this.buildOptOutPlotBands());
  }

  /**
   * Store original API data for timestamp lookups
   */
  private storeOriginalApiData(timeSeriesData: GraphTimeSeriesData[]): void {
    this.originalApiDataSignal.set(timeSeriesData);
  }

  /**
   * Build table series data from the current chart series,
   * joined by start_dttm_utc / end_dttm_utc across series.
   */
  private rebuildTableSeriesFromChart(): void {
    const frequency = this.apiMetadata()?.frequency ?? 15;
    this.tableSeriesSignal.set(this.graphService.buildTableSeries(this.chartSeriesSignal(), frequency, this.currencySignal()));
  }

  /**
   * PRT market only: build the capacity increased (green) and decreased (red)
   * marks shown on the graph.
   *
   * The values are reused directly from tableSeriesSignal (the same data the
   * table already shows), so there is one source of truth:
   *   - capacity.max -> increased  (green)
   *   - capacity.min -> decreased  (red)
   *
   * Returns an empty array for the Polish and other markets, so their graph is unchanged.
   */
  private getPrtCapacityMarkerSeries(): any[] {
    if (this.isConsolidatedToHourly() || this.isSplitDimsAbsentSignal()) return [];
    return this.graphService.buildCapacityMarkerSeries(this.tableSeriesSignal());
  }

  /**
   * PRT market tooltip. Grouped layout matching the design:
   *   - time-range header pill
   *   - Demand (single value)
   *   - Capacity: Increased (↑) / Decreased (↓)
   *   - Price:    Increased (↑) / Decreased (↓)
   *
   * All values come from tableSeriesSignal so the tooltip stays in sync with
   * the marks and the table (capacity.max/min and price.max/min). Price max/min
   * are currently equal until the split price mock lands in
   * rebuildTableSeriesFromChart — at which point this tooltip needs no change.
   */
  private buildPrtTooltipHtml(x: number): string {
    const offerDimensions = this.currentRegViewConfigSignal()?.graph_config?.offer_dimensions ?? [];
    const metadata = this.apiMetadata() || { frequency: 15 };
    const row = this.tableSeriesSignal().find((r) => moment.utc(r.offer_start_dttm_utc).valueOf() === x);
    const optedOut = this.resolveOptOutForTime(moment.utc(x)) === true;
    return this.graphService.buildPrtTooltipHtml({
      x,
      frequency: metadata.frequency || 15,
      timeZone: this.timeZoneSignal(),
      unit: metadata.unit || 'kW',
      currency: this.currencySignal(),
      row,
      optedOut,
      offerDimensions,
      showBaseline: this.showBaselineSignal()
    });
  }

  private getTickInterval(): number {
    const millisecondsPerMinute = 60 * 1000;
    const labelIntervalMinutes = 60;
    return labelIntervalMinutes * millisecondsPerMinute;
  }

  /**
   * Get base chart options configuration
   */
  private getBaseChartOptions(): Omit<Highcharts.Options, 'series'> {
    const tickInterval = this.getTickInterval();
    const metadata = this.apiMetadata();
    const componentRef = this;
    const userTimeZone = this.timeZoneSignal(); // Get user's timezone

    return {
      // Chart configuration with click event
      chart: {
        type: 'line',
        backgroundColor: '#ffffff',
        style: {
          fontFamily: 'Roobert, RoobertENEL-Light, sans-serif !important;'
        },
        height: 400,
        width: null,
        reflow: true,
        // Add click event handler
        events: {
          click: this.onChartClick
        }
      },

      // Add Demand plot options for series click events
      plotOptions: {
        line: {
          step: 'left',
          dataLabels: { enabled: false },
          enableMouseTracking: true,
          pointPlacement: 'on',
          marker: {
            enabled: false,
            states: {
              hover: { enabled: true, radius: 4 }
            }
          },
          // Add click event for line series
          point: {
            events: {
              click: function (event: any) {
                componentRef.onDataPointClick(this, event);
              }
            }
          }
        },
        column: {
          dataLabels: { enabled: false },
          enableMouseTracking: true,
          borderRadius: 0,
          pointPadding: 0,
          groupPadding: 0,
          borderWidth: 1,
          borderColor: '#ffffff',
          cursor: 'pointer',
          pointPlacement: 'between',
          states: {
            hover: { brightness: 0.1 }
          },
          // Add click event for column series
          point: {
            events: {
              click: function (event: any) {
                componentRef.onDataPointClick(this, event);
              }
            }
          }
        },
        scatter: {
          enableMouseTracking: true,
          cursor: 'pointer',
          pointPlacement: 'between',
          // Keep click-to-edit parity with the line/column series
          point: {
            events: {
              click: function (event: any) {
                componentRef.onDataPointClick(this, event);
              }
            }
          }
        },
        series: {
          turboThreshold: 3000,
          states: {
            hover: {
              enabled: true,
              lineWidthPlus: 1
            }
          }
        }
      },

      exporting: {
        enabled: true,
        fallbackToExportServer: false,
        buttons: {
          contextButton: {
            enabled: true,
            symbol: 'menu',
            menuItems: ['viewFullscreen', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG']
          }
        },
        menuItemDefinitions: {
          downloadPNG: {
            text: 'Download PNG',
            onclick: function () {
              componentRef.forceExportChart('image/png');
            }
          },
          downloadJPEG: {
            text: 'Download JPEG',
            onclick: function () {
              componentRef.forceExportChart('image/jpeg');
            }
          },
          downloadPDF: {
            text: 'Download PDF',
            onclick: function () {
              componentRef.forceExportChart('application/pdf');
            }
          },
          downloadSVG: {
            text: 'Download SVG',
            onclick: function () {
              componentRef.forceExportChart('image/svg+xml');
            }
          }
        },
        sourceWidth: 1280,
        sourceHeight: 500,
        scale: 2
      },

      title: {
        text: '',
        align: 'left',
        style: {
          color: '#333333',
          fontSize: '18px',
          fontWeight: 'bold'
        }
      },

      credits: { enabled: false },

      // X-axis configuration
      xAxis: {
        type: 'datetime',
        crosshair: true,
        tickWidth: 1,
        tickLength: 6,
        tickInterval: tickInterval,
        minTickInterval: tickInterval,
        plotBands: [...this.plotBandsSignal(), ...this.optOutPlotBandsSignal()],

        minorTickInterval: (metadata?.frequency ?? 15) * 60 * 1000,
        minorGridLineWidth: 1,
        minorGridLineColor: '#a5a3a3',
        minorGridLineDashStyle: 'Dot',
        minorTickLength: 0,

        labels: {
          style: {
            color: '#666666',
            fontSize: '12px'
          },
          formatter: function () {
            return moment.utc(this.value).tz(userTimeZone).format('HH:mm');
          },
          rotation: 0,
          overflow: 'justify',
          step: 1
        },
        title: {
          text: '',
          style: {
            color: '#666666',
            fontSize: '14px'
          }
        },
        gridLineWidth: 1,
        gridLineColor: '#f0f0f0'
      },

      // Y-axis configuration
      yAxis: {
        title: {
          text: metadata?.unit || 'Value',
          style: {
            color: '#666666',
            fontSize: '14px'
          }
        },
        labels: {
          style: {
            color: '#666666',
            fontSize: '12px'
          }
        },
        gridLineColor: '#e0e0e0',
        gridLineWidth: 1
      },

      legend: {
        enabled: false
      },

      // Tooltip configuration (existing)
      tooltip: {
        enabled: true,
        shared: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#cccccc',
        borderRadius: 5,
        shadow: true,
        useHTML: true,
        padding: 15,
        hideDelay: 500,
        distance: 30,
        style: {
          fontSize: '14px',
          fontFamily: 'Roobert, RoobertENEL-Light, sans-serif',
          lineHeight: '1.4',
          padding: '12px 16px',
          minWidth: '220px',
          maxWidth: '380px',
          minHeight: '80px',
          whiteSpace: 'normal',
          wordWrap: 'break-word'
        },
        formatter: function () {
          // PRT market: grouped tooltip (Demand / Capacity ↑↓ / Price ↑↓).
          // Polish and other market keeps the existing tooltip below, unchanged.
          if (!componentRef.isConsolidatedToHourly() && !componentRef.isSplitDimsAbsentSignal()) {
            return componentRef.buildPrtTooltipHtml(this.x as number);
          }

          const chartMetadata = componentRef.apiMetadata() || { frequency: 15 };
          const frequency = chartMetadata.frequency || 15;
          const userTz = componentRef.timeZoneSignal();

          // Convert to user's timezone
          const currentMoment = moment.utc(this.x).tz(userTz);
          const nextMoment = currentMoment.clone().add(frequency, 'minutes');
          const dateString = currentMoment.format('ddd Do MMMM');
          const timeRange = `${currentMoment.format('HH:mm')} - ${nextMoment.format('HH:mm')}`;

          let tooltip = `
            <div style="
              min-width: 14.375rem;
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 0.75rem;
              font-size: 0.75rem;
              color: #666;
              font-weight: 500;
            ">
              <span>${dateString}</span>
              <span>${timeRange}</span>
            </div>
          `;

          const optedOut = componentRef.resolveOptOutForTime(moment.utc(this.x as number)) === true;
          const optOutFooter = optedOut
            ? `<div style="margin-top:.5rem;padding-top:.5rem;border-top:1px solid #e0e3e8;font-size:0.8125rem;color:#E8384F;font-weight:600;">${componentRef.translate.instant('COM.TABS.OPEN.CHART.OFFER_OPTED_OUT')}</div>`
            : '';

          // Get all series data including invisible ones
          const allSeries = componentRef.chartSeriesSignal();
          const currentX = this.x;

          allSeries.forEach((series: DataSeries) => {
            const dataPoint = series.data.find((point) => point.x === currentX);
            if (dataPoint) {
              const value =
                dataPoint.y !== null && dataPoint.y !== undefined
                  ? `${dataPoint.y.toFixed(0)} ${series.displayName === 'Price' ? componentRef.currencySignal() : series.unit || ''}`
                  : '-';

              tooltip += `
              <div style="
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin: 0.5rem 0;
                font-size: 0.875rem;
                line-height: 1.4;
              ">
                <div style="
                  display: flex;
                  align-items: center;
                  color: #333;
                ">
                  <span style="
                    color: ${series.color};
                    margin-right: 0.5rem;
                    font-size: 0.75rem;
                    width: 0.5rem;
                    height: 0.5rem;
                    border-radius: 50%;
                    background-color: ${series.color};
                    display: inline-block;
                  "></span>
                  <span style="font-weight: 500;">${series.name}</span>
                </div>
                <div style="
                  font-weight: 600;
                  color: #333;
                  text-align: right;
                ">
                  ${value}
                </div>
              </div>
            `;
            }
          });

          return `
            <div style="
              min-width: 11.25rem;
              padding: 0.75rem;
              line-height: 1.4;
            ">
              ${tooltip}
              ${optOutFooter}
            </div>
          `;
        }
      }
    } as Highcharts.Options;
  }

  /**
   * Chart reference callback for highcharts-angular
   */
  chartCallback: Highcharts.ChartCallbackFunction = (chart: Highcharts.Chart): void => {
    this.chartRef = chart;

    // Only update if we have data
    if (this.hasDataSignal() && this.chartSeriesSignal().length > 0) {
      this.updateChartManually();
    }
  };

  get getChipStyle() {
    return {
      'background-color': 'rgb(245, 246, 250)',
      'border-color': 'rgb(182, 195, 213)',
      color: '#000'
    };
  }

  private updateChartManually(): void {
    if (!this.chartRef) {
      console.log('Chart ref not available yet');
      return;
    }

    while (this.chartRef.series.length > 0) {
      this.chartRef.series[0].remove(false);
    }

    const visibleSeries = this.chartSeriesSignal().filter((series: DataSeries) => series.visible && series.id !== OpenGraphService.BASELINE_DIM_ID);

    visibleSeries.forEach((series: DataSeries) => {
      const seriesOptions: any = {
        id: series.id,
        name: series.name,
        data: series.data,
        color: series.color,
        type: series.chartType,
        unit: series.unit
      };

      if (series.chartType === 'line') {
        seriesOptions.lineWidth = 2;
        seriesOptions.marker = {
          enabled: false,
          states: {
            hover: { enabled: true, radius: 4 }
          }
        };
      } else if (series.chartType === 'column') {
        seriesOptions.borderWidth = 1;
        seriesOptions.borderColor = '#ffffff';
        seriesOptions.pointPadding = 0;
        seriesOptions.groupPadding = 0;
        seriesOptions.borderRadius = 0;
      }

      this.chartRef!.addSeries(seriesOptions, false);
    });

    this.getBaselineLineSeries().forEach((s) => this.chartRef!.addSeries(s, false));

    // PRT market: add the capacity increased/decreased marks (no-op for Polish)
    this.getPrtCapacityMarkerSeries().forEach((markerSeries) => this.chartRef!.addSeries(markerSeries, false));

    this.chartRef.redraw();
  }

  private forceExportChart(exportType: 'image/png' | 'image/jpeg' | 'application/pdf' | 'image/svg+xml'): void {
    if (!this.chartRef || !this.hasDataSignal()) {
      return;
    }

    const currentOptions: Highcharts.Options = this.chartOptions();

    const exportSeries = this.chartSeriesSignal()
      .filter((series: DataSeries) => series.visible && series.id !== OpenGraphService.BASELINE_DIM_ID)
      .map((series: DataSeries) => {
        const seriesOptions: any = {
          id: series.id,
          name: series.name,
          data: series.data,
          color: series.color,
          type: series.chartType
        };

        if (series.chartType === 'column') {
          seriesOptions.borderWidth = 1;
          seriesOptions.borderColor = '#ffffff';
          seriesOptions.pointPadding = 0;
          seriesOptions.groupPadding = 0;
        }

        return seriesOptions;
      });

    const exportOptions: Highcharts.Options = {
      ...currentOptions,
      chart: currentOptions.chart,
      series: [...exportSeries, ...this.getBaselineLineSeries(), ...this.getPrtCapacityMarkerSeries()]
    };

    const tempDiv = document.createElement('div');
    tempDiv.style.width = '1200px';
    tempDiv.style.height = '600px';
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    document.body.appendChild(tempDiv);

    const tempChart = Highcharts.chart(tempDiv, exportOptions);

    tempChart.exportChart(
      {
        type: exportType as any,
        filename: `offers-${moment().format('YYYY-MM-DD_HHmmss')}`
      },
      {}
    );

    tempChart.destroy();
    document.body.removeChild(tempDiv);
  }

  openExportFunctionalityDialog() {
    //TODO: Add min and max date to the dialog, missing info on the data
    this.dataVMService.openExportFunctionalityDialog(undefined, undefined, undefined);
  }

  openBulkUploadFunctionalityDialog(): void {
    const regConfig = this.currentRegViewConfigSignal();
    const currentSelectedDate = this.dataVMService.selectedFromDayPickerFormControl.value?.start;
    this.dataVMService.openBulkUploadFunctionalityDialog({
      minDate: currentSelectedDate,
      maxDate: undefined,
      registrationId: regConfig?.registrationId ?? '',
      intervalFrequency: regConfig?.intervalFrequency ?? 60,
      timeZone: regConfig?.timeZone ?? 'UTC',
      importType: BulkImportType.Offers,
      registrationName: regConfig?.display_labels ?? '',
      offer_dimensions: regConfig?.graph_config?.offer_dimensions ?? null
    });
  }

  clickOutsideDownloadUploadOptions(): void {
    this.showDownloadUploadOptionsSignal.update((show) => {
      return show ? false : show;
    });
  }

  toggleDownloadUploadOptions(event: MouseEvent): void {
    event.stopPropagation();
    this.showDownloadUploadOptionsSignal.update((show) => !show);
  }

  private getBaselineLineSeries(): any[] {
    if (!this.showBaselineSignal()) return [];
    return this.graphService.buildBaselineLineSeries(this.originalApiDataSignal(), '#000000');
  }
}
